import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, ChevronRight, ChevronDown, AlertTriangle, Phone, User, ExternalLink, CheckCircle2, Clock, Cloud, FileText, AlertCircle, ClipboardList, Eye, Users, Lightbulb, Database, Shield, RotateCcw, Calendar, Building2, Download, Mail, Share2, Copy, MessageCircle, Sparkles, ShieldAlertIcon, Zap, Plus } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Toast } from './Toast';
import { RiskLevel, RiskStatus, DataSource, CorrectiveAction, NearMissReport, PTPDocument, getRiskBand, RiskFactorScores, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, FactorSeverity } from './RiskAreasWidget';
import { EnrichedRiskArea } from '../types/prevention';
import { RiskAIChat } from './RiskAIChat';
import { PTPDocumentViewer } from './PTPDocumentViewer';
import { RiskScoreBreakdown } from './RiskScoreBreakdown';
import { usePreventionModel } from '../contexts/PreventionModelContext';
const STATUS_CONFIG: Record<RiskStatus, {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ElementType;
}> = {
  open: {
    label: 'Open',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: AlertCircle
  },
  measures_taken: {
    label: 'Measures Taken',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: Clock
  },
  closed: {
    label: 'Closed',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    icon: CheckCircle2
  },
  monitoring: {
    label: 'Monitoring',
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    icon: Eye
  }
};
const RISK_LEVEL_CONFIG: Record<RiskLevel, {
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
}> = {
  critical: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300',
    label: 'Critical Risk'
  },
  high: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
    label: 'High Risk'
  },
  medium: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
    label: 'Medium Risk'
  },
  low: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-300',
    label: 'Low Risk'
  }
};
const DATA_SOURCE_ICONS: Record<DataSource['type'], React.ReactNode> = {
  weather: <Cloud size={14} data-id="element-3131" />,
  inspection: <ClipboardList size={14} data-id="element-3132" />,
  incident: <AlertCircle size={14} data-id="element-3133" />,
  document: <FileText size={14} data-id="element-3134" />,
  observation: <Eye size={14} data-id="element-3135" />,
  worker: <Users size={14} data-id="element-3136" />,
  ptp: <ClipboardList size={14} data-id="element-3137" />
};
const PRIORITY_CONFIG: Record<CorrectiveAction['priority'] | 'high' | 'medium' | 'low', {
  label: string;
  color: string;
  bgColor: string;
}> = {
  immediate: {
    label: 'Immediate',
    color: 'text-red-700',
    bgColor: 'bg-red-100'
  },
  today: {
    label: 'Today',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100'
  },
  this_week: {
    label: 'This Week',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100'
  },
  high: {
    label: 'High',
    color: 'text-red-700',
    bgColor: 'bg-red-100'
  },
  medium: {
    label: 'Medium',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100'
  },
  low: {
    label: 'Low',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100'
  }
};
const SEVERITY_CONFIG: Record<NearMissReport['severity'], {
  color: string;
  bgColor: string;
  label: string;
}> = {
  high: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'High'
  },
  medium: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    label: 'Medium'
  },
  low: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    label: 'Low'
  }
};
const INCIDENT_STATUS_CONFIG: Record<NearMissReport['status'], {
  color: string;
  bgColor: string;
  label: string;
}> = {
  open: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'Open'
  },
  investigating: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    label: 'Investigating'
  },
  resolved: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    label: 'Resolved'
  }
};
interface RiskAreaDetailModalProps {
  riskArea: EnrichedRiskArea | null;
  onClose: () => void;
  onUpdateStatus: (areaId: string, newStatus: RiskStatus, notes: string, userName: string) => void;
  onOpenMethodology?: () => void;
}
function generateReportText(riskArea: EnrichedRiskArea): string {
  const config = RISK_LEVEL_CONFIG[riskArea.riskLevel];
  const statusConfig = STATUS_CONFIG[riskArea.status];
  const band = getRiskBand(riskArea.riskScore);
  const lines: string[] = [];
  lines.push('═══════════════════════════════════════');
  lines.push('RISK AREA REPORT');
  lines.push('═══════════════════════════════════════');
  lines.push('');
  lines.push(`Risk Level: ${config.label.toUpperCase()}`);
  lines.push(`Risk Band: ${band.label} (${band.range})`);
  lines.push(`Status: ${statusConfig.label.toUpperCase()}`);
  lines.push(`Generated: ${new Date().toLocaleString()}`);
  lines.push('');
  lines.push('── CONTRIBUTING FACTORS ──');
  (Object.entries(riskArea.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).forEach(([factor, severity]) => {
    if (severity !== 'none') {
      lines.push(`• ${FACTOR_CONFIG[factor].label}: ${severity.toUpperCase()}`);
    }
  });
  lines.push('');
  lines.push('── LOCATION ──');
  lines.push(`Project: ${riskArea.project}`);
  lines.push(`Floor: ${riskArea.floor}`);
  lines.push(`Area: ${riskArea.location}`);
  lines.push(`Contractors: ${riskArea.contractors.join(', ')}`);
  lines.push('');
  lines.push('── RISK FACTORS ──');
  riskArea.riskFactors.forEach((factor, i) => {
    lines.push(`${i + 1}. ${factor}`);
  });
  lines.push('');
  lines.push('── DATA SOURCES ──');
  riskArea.dataSources.forEach(source => {
    lines.push(`• ${source.name}: ${source.description}`);
  });
  lines.push('');
  lines.push('── CORRECTIVE ACTIONS ──');
  riskArea.correctiveActions.forEach(action => {
    const priority = PRIORITY_CONFIG[action.priority];
    lines.push(`[${priority.label}] ${action.action}${action.assignedTo ? ` (Assigned: ${action.assignedTo})` : ''}`);
  });
  lines.push('');
  lines.push('── CONTACTS ──');
  riskArea.contacts.forEach(contact => {
    lines.push(`• ${contact.name} — ${contact.role}, ${contact.company} — ${contact.phone}`);
  });
  if (riskArea.measuresRecord) {
    lines.push('');
    lines.push('── MEASURES TAKEN ──');
    lines.push(`By: ${riskArea.measuresRecord.takenBy}`);
    lines.push(`Date: ${riskArea.measuresRecord.takenAt.toLocaleString()}`);
    lines.push(`Notes: ${riskArea.measuresRecord.notes}`);
  }
  if (riskArea.treatment) {
    lines.push('');
    lines.push('── CLOSURE RECORD ──');
    lines.push(`Closed by: ${riskArea.treatment.treatedBy}`);
    lines.push(`Date: ${riskArea.treatment.treatedAt.toLocaleString()}`);
    lines.push(`Notes: ${riskArea.treatment.notes}`);
  }
  lines.push('');
  lines.push('───────────────────────────────────────');
  lines.push(`Report ID: RISK-${riskArea.id} | Last updated: ${riskArea.lastUpdated.toLocaleString()}`);
  return lines.join('\n');
}
function generateReportHTML(riskArea: EnrichedRiskArea): string {
  const levelColors: Record<RiskLevel, string> = {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#d97706',
    low: '#059669'
  };
  const color = levelColors[riskArea.riskLevel];
  const config = RISK_LEVEL_CONFIG[riskArea.riskLevel];
  const statusConfig = STATUS_CONFIG[riskArea.status];
  const band = getRiskBand(riskArea.riskScore);
  const css = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1f2937; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { border-bottom: 3px solid ${color}; padding-bottom: 20px; margin-bottom: 24px; }
    .header h1 { font-size: 24px; margin-bottom: 8px; }
    .badge { display: inline-block; background: ${color}; color: white; padding: 4px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; margin-right: 8px; }
    .score { display: inline-block; background: #f3f4f6; padding: 4px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; }
    .status { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; margin-left: 8px; border: 1px solid #e5e7eb; }
    .meta { color: #6b7280; font-size: 13px; margin-top: 8px; }
    .location { font-size: 16px; font-weight: 600; margin: 12px 0 4px; }
    .location-detail { color: #6b7280; font-size: 14px; }
    .section { margin-top: 24px; }
    .section h2 { font-size: 16px; font-weight: 700; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; }
    .factor { padding: 8px 12px; background: #fef2f2; border-left: 3px solid ${color}; margin-bottom: 8px; font-size: 14px; border-radius: 0 4px 4px 0; }
    .source { padding: 8px 12px; background: #f9fafb; border: 1px solid #e5e7eb; margin-bottom: 6px; border-radius: 4px; font-size: 14px; }
    .source strong { display: block; font-size: 13px; }
    .source span { color: #6b7280; font-size: 12px; }
    .action { padding: 8px 12px; border: 1px solid #e5e7eb; margin-bottom: 6px; border-radius: 4px; font-size: 14px; display: flex; justify-content: space-between; align-items: center; }
    .action .priority { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 4px; }
    .priority-immediate { background: #fef2f2; color: #b91c1c; }
    .priority-today { background: #fff7ed; color: #c2410c; }
    .priority-this_week { background: #fffbeb; color: #b45309; }
    .contact { display: inline-block; padding: 8px 16px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; margin-right: 8px; margin-bottom: 8px; font-size: 13px; }
    .contact strong { display: block; }
    .contact .role { color: #6b7280; font-size: 12px; }
    .record { padding: 12px 16px; border-radius: 6px; margin-top: 16px; }
    .record-measures { background: #fffbeb; border: 1px solid #fde68a; }
    .record-closed { background: #ecfdf5; border: 1px solid #a7f3d0; }
    .record h3 { font-size: 14px; margin-bottom: 6px; }
    .record-measures h3 { color: #92400e; }
    .record-closed h3 { color: #065f46; }
    .record p { font-size: 13px; }
    .record-measures p { color: #b45309; }
    .record-closed p { color: #047857; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px; display: flex; justify-content: space-between; }
    @media print { body { padding: 20px; } }
  `;
  return `<!DOCTYPE html>
<html>
<head>
  <title>Risk Report — ${riskArea.project} ${riskArea.floor}</title>
  <style>{`;
  $;
  {
    css;
  }
  ;
  `}</style>
</head>
<body>
  <div class="header">
    <h1>Risk Area Report</h1>
    <div>
      <span class="badge">${config.label}</span>
      <span class="score">Band: ${band.label} (${band.range})</span>
      <span class="status">${statusConfig.label}</span>
    </div>
    <div class="location">${riskArea.project} › ${riskArea.floor} › ${riskArea.location}</div>
    <div class="location-detail">Contractors: ${riskArea.contractors.join(' · ')}</div>
    <div class="meta">Generated: ${new Date().toLocaleString()}</div>
  </div>

  <div class="section">
    <h2>Risk Factors</h2>
    ${riskArea.riskFactors.map((f, i) => `<div class="factor">${i + 1}. ${f}</div>`).join('')}
  </div>

  <div class="section">
    <h2>Data Sources</h2>
    ${riskArea.dataSources.map(s => `<div class="source"><strong>${s.name}</strong><span>${s.description}</span></div>`).join('')}
  </div>

  <div class="section">
    <h2>Corrective Actions</h2>
    ${riskArea.correctiveActions.map(a => `<div class="action"><span>${a.action}${a.assignedTo ? ` <em style="color:#6b7280">(${a.assignedTo})</em>` : ''}</span><span class="priority priority-${a.priority}">${PRIORITY_CONFIG[a.priority].label}</span></div>`).join('')}
  </div>

  <div class="section">
    <h2>Contractor Contacts</h2>
    ${riskArea.contacts.map(c => `<div class="contact"><strong>${c.name}</strong><span class="role">${c.role} · ${c.company}</span><br/>${c.phone}</div>`).join('')}
  </div>

  ${riskArea.measuresRecord ? `
  <div class="record record-measures">
    <h3>Measures Taken</h3>
    <p>${riskArea.measuresRecord.notes}</p>
    <p style="margin-top:6px;font-size:12px;opacity:0.8">By ${riskArea.measuresRecord.takenBy} · ${riskArea.measuresRecord.takenAt.toLocaleString()}</p>
  </div>` : ''}

  ${riskArea.treatment ? `
  <div class="record record-closed">
    <h3>Closure Record</h3>
    <p>${riskArea.treatment.notes}</p>
    <p style="margin-top:6px;font-size:12px;opacity:0.8">By ${riskArea.treatment.treatedBy} · ${riskArea.treatment.treatedAt.toLocaleString()}</p>
  </div>` : ''}

  <div class="footer">
    <span>Risk ID: RISK-${riskArea.id}</span>
    <span>Last updated: ${riskArea.lastUpdated.toLocaleString()}</span>
  </div>
</body>
</html>`;
}
export function RiskAreaDetailModal({
  riskArea,
  onClose,
  onUpdateStatus,
  onOpenMethodology
}: RiskAreaDetailModalProps) {
  useEscapeKey(onClose);
  const {
    addActionItem
  } = usePreventionModel();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [statusNotes, setStatusNotes] = useState('');
  const [activeForm, setActiveForm] = useState<'measures' | 'close' | 'reopen' | null>(null);
  const [formNotes, setFormNotes] = useState('');
  const [userName, setUserName] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [expandedWorkers, setExpandedWorkers] = useState<string | null>(null);
  const [selectedNearMissReport, setSelectedNearMissReport] = useState<NearMissReport | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [showGeneralFeedback, setShowGeneralFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('Risk assessment feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedPTPDocument, setSelectedPTPDocument] = useState<PTPDocument | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);
  // State for Create Action Item
  const [creatingAction, setCreatingAction] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionOwner, setActionOwner] = useState('');
  const [actionDate, setActionDate] = useState('');
  const [actionPriority, setActionPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const handleCreateAction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!riskArea || !actionTitle || !actionOwner || !actionDate) return;
    addActionItem({
      title: actionTitle,
      description: `Action created from hotspot: ${riskArea.title}`,
      type: 'hotspot_mitigation',
      triggerSource: 'hotspot',
      triggerSourceId: riskArea.id,
      hotspotId: riskArea.id,
      owner: actionOwner,
      priority: actionPriority,
      dueDate: actionDate,
      status: 'open',
      rationale: 'Manually created to address hotspot risk.',
      expectedImpact: 'Mitigate identified risk factors.'
    });
    setCreatingAction(false);
    setActionTitle('');
    setActionOwner('');
    setActionDate('');
    setActionPriority('medium');
    // Show toast
    setToast({
      show: true,
      message: 'Action item created successfully',
      type: 'success'
    });
  };
  const shareMenuRef = useRef<HTMLDivElement>(null);
  // --- NEW STATE FOR PROGRESSIVE DISCLOSURE ---
  const [reviewedSections, setReviewedSections] = useState<Set<string>>(new Set());
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const toggleSection = (section: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);else next.add(section);
      return next;
    });
    setReviewedSections(prev => new Set(prev).add(section));
  };
  // --------------------------------------------
  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);
  if (!riskArea) return null;
  const config = RISK_LEVEL_CONFIG[riskArea.riskLevel];
  const statusConfig = STATUS_CONFIG[riskArea.status];
  const StatusIcon = statusConfig.icon;
  // --- COMPUTED VALUES FOR QUICK OVERVIEW ---
  const activeFactors = (Object.entries(riskArea.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).filter(([_, sev]) => sev !== 'none').sort((a, b) => {
    const order = {
      high: 0,
      medium: 1,
      low: 2,
      none: 3
    };
    return order[a[1]] - order[b[1]];
  });
  const topFactor = activeFactors[0];
  const sortedActions = [...riskArea.correctiveActions].sort((a, b) => {
    const order = {
      immediate: 0,
      today: 1,
      this_week: 2
    };
    return order[a.priority] - order[b.priority];
  });
  const topAction = sortedActions[0];
  const allReviewed = reviewedSections.size === 5;
  const remainingToReview = 5 - reviewedSections.size;
  // ------------------------------------------
  const resetForm = () => {
    setActiveForm(null);
    setFormNotes('');
    setUserName('');
  };
  const handleSubmitStatusUpdate = (newStatus: RiskStatus) => {
    if (!formNotes.trim() || !userName.trim()) return;
    onUpdateStatus(riskArea.id, newStatus, formNotes, userName);
    let msg = '';
    if (newStatus === 'measures_taken') msg = 'Measures recorded successfully';else if (newStatus === 'closed') msg = 'Risk area marked as closed';else if (newStatus === 'open') msg = 'Risk area reopened';
    setToastMessage(msg);
    setShowToast(true);
    resetForm();
  };
  const handleContactClick = (phone: string, name: string) => {
    setToastMessage(`Calling ${name}...`);
    setShowToast(true);
  };
  const handleDataSourceClick = (source: DataSource) => {
    if (source.type === 'weather' && source.link) {
      window.open(source.link, '_blank', 'noopener,noreferrer');
    } else if (source.type === 'incident' && source.nearMissReports && source.nearMissReports.length > 0) {
      setSelectedNearMissReport(source.nearMissReports[0]);
    } else if (source.type === 'worker' && source.workers) {
      setExpandedWorkers(expandedWorkers === source.id ? null : source.id);
    } else if (source.type === 'ptp' && source.ptpDocument) {
      setSelectedPTPDocument(source.ptpDocument);
    }
  };
  const isClickableSource = (source: DataSource) => {
    return source.type === 'weather' && source.link || source.type === 'incident' && source.nearMissReports && source.nearMissReports.length > 0 || source.type === 'worker' && source.workers && source.workers.length > 0 || source.type === 'ptp' && !!source.ptpDocument;
  };
  const handleDownloadPDF = () => {
    const html = generateReportHTML(riskArea);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 300);
    }
    setShowShareMenu(false);
    setToastMessage('Report opened for printing/PDF download');
    setShowToast(true);
  };
  const handleShareEmail = () => {
    setShowEmailForm(true);
    setShowShareMenu(false);
  };
  const handleSendEmail = () => {
    const reportText = generateReportText(riskArea);
    const subject = encodeURIComponent(`Risk Report: ${riskArea.project} — ${riskArea.floor} — ${riskArea.location} [${RISK_LEVEL_CONFIG[riskArea.riskLevel].label}]`);
    const body = encodeURIComponent(`${emailNote ? emailNote + '\n\n---\n\n' : ''}${reportText}`);
    const mailto = `mailto:${encodeURIComponent(emailTo)}?subject=${subject}&body=${body}`;
    window.open(mailto);
    setShowEmailForm(false);
    setEmailTo('');
    setEmailNote('');
    setToastMessage('Email client opened with report');
    setShowToast(true);
  };
  const handleCopyReport = () => {
    const reportText = generateReportText(riskArea);
    navigator.clipboard.writeText(reportText).then(() => {
      setToastMessage('Report copied to clipboard');
      setShowToast(true);
    }).catch(() => {
      setToastMessage('Failed to copy — try again');
      setShowToast(true);
    });
    setShowShareMenu(false);
  };
  const submitGeneralFeedback = () => {
    if (!feedbackText.trim()) return;
    setToastMessage('Feedback submitted. Thank you!');
    setShowToast(true);
    setShowGeneralFeedback(false);
    setFeedbackText('');
  };
  return <>
      <AnimatePresence data-id="element-3138">
        {riskArea && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-3139">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-3140" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5,
          bounce: 0.3
        }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-3141">
              {/* SIF Banner */}
              {riskArea.sifPotential && <div className="bg-red-800 text-white px-6 py-4 flex items-center gap-3 flex-shrink-0 animate-pulse" data-id="element-3142">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-3143">
                    <ShieldAlertIcon size={22} data-id="element-3144" />
                  </div>
                  <div data-id="element-3145">
                    <span className="font-black text-sm uppercase tracking-wider block" data-id="element-3146">
                      ⚠️ SIF Potential — Serious Injury or Fatality Risk
                    </span>
                    <span className="text-red-200 text-xs" data-id="element-3147">
                      This hotspot has been flagged for potential
                      life-threatening outcomes. Prioritize immediate review.
                    </span>
                  </div>
                </div>}

              {/* Header */}
              <div className={`p-6 border-b ${config.borderColor} ${config.bgColor}`} data-id="element-3148">
                <div className="flex items-start justify-between" data-id="element-3149">
                  <div className="flex-1" data-id="element-3150">
                    <div className="flex items-center gap-3 mb-2" data-id="element-3151">
                      <div className="flex items-center gap-1 bg-white/80 px-2.5 py-1 rounded-full" data-id="element-3152">
                        <AlertTriangle size={14} className={config.color} data-id="element-3153" />
                        <span className={`text-sm font-bold ${config.color}`} data-id="element-3154">
                          {getRiskBand(riskArea.riskScore).label} (
                          {getRiskBand(riskArea.riskScore).range})
                        </span>
                      </div>

                      {/* Status Badge */}
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/50 bg-white/80 ${statusConfig.color}`} data-id="element-3155">
                        <StatusIcon size={14} data-id="element-3156" />
                        <span className="text-sm font-semibold" data-id="element-3157">
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-lg font-bold text-gray-900 mb-1" data-id="element-3158">
                      <MapPin size={18} className="text-gray-500" data-id="element-3159" />
                      <span data-id="element-3160">{riskArea.project}</span>
                      <ChevronRight size={16} className="text-gray-400" data-id="element-3161" />
                      <span className="text-gray-700" data-id="element-3162">{riskArea.floor}</span>
                      <ChevronRight size={16} className="text-gray-400" data-id="element-3163" />
                      <span className="text-gray-600 font-medium" data-id="element-3164">
                        {riskArea.location}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3165">
                      <Users size={14} data-id="element-3166" />
                      <span data-id="element-3167">{riskArea.contractors.join(' · ')}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1" data-id="element-3168">
                    {/* Share/Report Button */}
                    <div className="relative" ref={shareMenuRef} data-id="element-3169">
                      <button onClick={() => setShowShareMenu(!showShareMenu)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-colors" title="Generate report" data-id="element-3170">
                        <Share2 size={18} data-id="element-3171" />
                      </button>

                      <AnimatePresence data-id="element-3172">
                        {showShareMenu && <motion.div initial={{
                      opacity: 0,
                      y: -8,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      y: -8,
                      scale: 0.95
                    }} transition={{
                      duration: 0.15
                    }} className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" data-id="element-3173">
                            <button onClick={handleDownloadPDF} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3174">
                              <Download size={16} className="text-gray-400" data-id="element-3175" />
                              <div data-id="element-3176">
                                <p className="font-medium" data-id="element-3177">Download PDF</p>
                                <p className="text-xs text-gray-500" data-id="element-3178">
                                  Print or save as PDF
                                </p>
                              </div>
                            </button>
                            <button onClick={handleShareEmail} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3179">
                              <Mail size={16} className="text-gray-400" data-id="element-3180" />
                              <div data-id="element-3181">
                                <p className="font-medium" data-id="element-3182">Share via Email</p>
                                <p className="text-xs text-gray-500" data-id="element-3183">
                                  Send report to a recipient
                                </p>
                              </div>
                            </button>
                            <button onClick={handleCopyReport} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3184">
                              <Copy size={16} className="text-gray-400" data-id="element-3185" />
                              <div data-id="element-3186">
                                <p className="font-medium" data-id="element-3187">Copy to Clipboard</p>
                                <p className="text-xs text-gray-500" data-id="element-3188">
                                  Copy report as text
                                </p>
                              </div>
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-colors" data-id="element-3189">
                      <X size={20} data-id="element-3190" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Email Form Banner */}
              <AnimatePresence data-id="element-3191">
                {showEmailForm && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="border-b border-gray-200 bg-blue-50 overflow-hidden" data-id="element-3192">
                    <div className="p-4 space-y-3" data-id="element-3193">
                      <div className="flex items-center gap-2 mb-1" data-id="element-3194">
                        <Mail size={16} className="text-blue-600" data-id="element-3195" />
                        <h4 className="font-semibold text-gray-900" data-id="element-3196">
                          Share Report via Email
                        </h4>
                      </div>
                      <div data-id="element-3197">
                        <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-3198">
                          Recipient Email
                        </label>
                        <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="name@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-3199" />
                      </div>
                      <div data-id="element-3200">
                        <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-3201">
                          Add a Note (optional)
                        </label>
                        <textarea value={emailNote} onChange={e => setEmailNote(e.target.value)} placeholder="Hi, please review this risk area report..." rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-3202" />
                      </div>
                      <div className="flex gap-2" data-id="element-3203">
                        <button onClick={() => {
                    setShowEmailForm(false);
                    setEmailTo('');
                    setEmailNote('');
                  }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-3204">
                          Cancel
                        </button>
                        <button onClick={handleSendEmail} disabled={!emailTo.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-3205">
                          <Mail size={14} data-id="element-3206" />
                          Open Email Client
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-3207">
                {/* Status Records Display */}
                {riskArea.status === 'measures_taken' && riskArea.measuresRecord && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-3208">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3209">
                        <Clock size={18} className="text-amber-600" data-id="element-3210" />
                        <h3 className="font-semibold text-amber-900" data-id="element-3211">
                          Measures Taken
                        </h3>
                      </div>
                      <p className="text-sm text-amber-800 mb-2" data-id="element-3212">
                        {riskArea.measuresRecord.notes}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-amber-600" data-id="element-3213">
                        <span className="flex items-center gap-1" data-id="element-3214">
                          <User size={12} data-id="element-3215" />
                          {riskArea.measuresRecord.takenBy}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-3216">
                          <Clock size={12} data-id="element-3217" />
                          {riskArea.measuresRecord.takenAt.toLocaleString()}
                        </span>
                      </div>
                    </motion.div>}

                {riskArea.status === 'closed' && riskArea.treatment && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-id="element-3218">
                    <div className="flex items-center gap-2 mb-2" data-id="element-3219">
                      <Shield size={18} className="text-emerald-600" data-id="element-3220" />
                      <h3 className="font-semibold text-emerald-900" data-id="element-3221">
                        Risk Closed
                      </h3>
                    </div>
                    <p className="text-sm text-emerald-800 mb-2" data-id="element-3222">
                      {riskArea.treatment.notes}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-emerald-600" data-id="element-3223">
                      <span className="flex items-center gap-1" data-id="element-3224">
                        <User size={12} data-id="element-3225" />
                        {riskArea.treatment.treatedBy}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-3226">
                        <Clock size={12} data-id="element-3227" />
                        {riskArea.treatment.treatedAt.toLocaleString()}
                      </span>
                    </div>
                  </motion.div>}

                {/* Reopen History */}
                {riskArea.reopenHistory && riskArea.reopenHistory.length > 0 && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-3228">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3229">
                        <RotateCcw size={16} className="text-gray-600" data-id="element-3230" />
                        <h4 className="text-sm font-semibold text-gray-900" data-id="element-3231">
                          Reopen History
                        </h4>
                      </div>
                      <div className="space-y-2" data-id="element-3232">
                        {riskArea.reopenHistory.map((record, index) => <div key={index} className="text-xs text-gray-700 bg-white border border-gray-100 rounded p-2" data-id="element-3233">
                            <p className="font-medium" data-id="element-3234">{record.reason}</p>
                            <p className="text-gray-500 mt-1" data-id="element-3235">
                              Reopened by {record.reopenedBy} ·{' '}
                              {record.reopenedAt.toLocaleString()}
                            </p>
                          </div>)}
                      </div>
                    </div>}

                {/* Quick Overview Card */}
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 shadow-sm" data-id="element-3236">
                  <div className="flex items-center gap-2 mb-4" data-id="element-3237">
                    <Zap size={18} className="text-indigo-600" data-id="element-3238" />
                    <h3 className="font-bold text-indigo-900" data-id="element-3239">
                      Quick Overview
                    </h3>
                  </div>

                  <div className="space-y-4" data-id="element-3240">
                    {topFactor && <div data-id="element-3241">
                        <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wider mb-1.5" data-id="element-3242">
                          Primary Risk Driver
                        </p>
                        <div className="flex items-center gap-2 mb-1" data-id="element-3243">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${FACTOR_SEVERITY_COLORS[topFactor[1]].bg} ${FACTOR_SEVERITY_COLORS[topFactor[1]].text}`} data-id="element-3244">
                            {FACTOR_CONFIG[topFactor[0]].short}
                          </span>
                          <p className="text-sm font-bold text-gray-900" data-id="element-3245">
                            {FACTOR_CONFIG[topFactor[0]].label}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-3246">
                          {riskArea.riskFactors[0]}
                        </p>
                      </div>}

                    {topAction && <div className="pt-4 border-t border-indigo-100/50" data-id="element-3247">
                        <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wider mb-1.5" data-id="element-3248">
                          Top Corrective Action
                        </p>
                        <div className="flex items-start gap-2" data-id="element-3249">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold mt-0.5 ${PRIORITY_CONFIG[topAction.priority].bgColor} ${PRIORITY_CONFIG[topAction.priority].color}`} data-id="element-3250">
                            {PRIORITY_CONFIG[topAction.priority].label}
                          </span>
                          <p className="text-sm font-medium text-gray-900" data-id="element-3251">
                            {topAction.action}
                          </p>
                        </div>
                      </div>}

                    <div className="pt-4 border-t border-indigo-100/50 flex items-center gap-6" data-id="element-3252">
                      <div className="flex items-center gap-1.5" data-id="element-3253">
                        <Clock size={14} className="text-indigo-400" data-id="element-3254" />
                        <span className="text-xs font-medium text-indigo-800" data-id="element-3255">
                          Updated{' '}
                          {riskArea.lastUpdated.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-3256">
                        <Database size={14} className="text-indigo-400" data-id="element-3257" />
                        <span className="text-xs font-medium text-indigo-800 capitalize" data-id="element-3258">
                          {riskArea.dataConfidence} confidence
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Collapsible Sections */}
                <div className="space-y-3" data-id="element-3259">
                  {/* Section 1: Risk Factors */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3260">
                    <button onClick={() => toggleSection('factors')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3261">
                      <div className="flex items-center gap-3" data-id="element-3262">
                        <AlertTriangle size={18} className="text-gray-500" data-id="element-3263" />
                        <h3 className="font-bold text-gray-900" data-id="element-3264">
                          All Risk Factors
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3265">
                        {reviewedSections.has('factors') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3266" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3267" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('factors') ? 'rotate-180' : ''}`} data-id="element-3268" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3269">
                      {openSections.has('factors') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3270">
                          <div className="p-5 border-t border-gray-200 space-y-6" data-id="element-3271">
                            <RiskScoreBreakdown scores={riskArea.riskFactorScores} riskScore={riskArea.riskScore} onTuneWeights={onOpenMethodology} data-id="element-3272" />
                            <div data-id="element-3273">
                              <h4 className="font-bold text-gray-900 mb-3" data-id="element-3274">
                                Why This Area is Risky
                              </h4>
                              <div className="space-y-2" data-id="element-3275">
                                {riskArea.riskFactors.map((factor, index) => <div key={index} className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-lg" data-id="element-3276">
                                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3277">
                                      <span className="text-xs font-bold text-red-600" data-id="element-3278">
                                        {index + 1}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-800" data-id="element-3279">
                                      {factor}
                                    </p>
                                  </div>)}
                              </div>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 2: Data Sources */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3280">
                    <button onClick={() => toggleSection('dataSources')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3281">
                      <div className="flex items-center gap-3" data-id="element-3282">
                        <Database size={18} className="text-gray-500" data-id="element-3283" />
                        <h3 className="font-bold text-gray-900" data-id="element-3284">
                          Data Sources
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3285">
                        {reviewedSections.has('dataSources') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3286" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3287" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('dataSources') ? 'rotate-180' : ''}`} data-id="element-3288" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3289">
                      {openSections.has('dataSources') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3290">
                          <div className="p-5 border-t border-gray-200 space-y-2" data-id="element-3291">
                            {riskArea.dataSources.map((source, index) => {
                        const isClickable = isClickableSource(source);
                        const isExpanded = expandedWorkers === source.id;
                        return <div key={source.id} data-id="element-3292">
                                  <button onClick={() => isClickable && handleDataSourceClick(source)} disabled={!isClickable} className={`w-full flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-left transition-colors ${isClickable ? 'hover:bg-gray-100 hover:border-gray-300 cursor-pointer' : 'cursor-default'}`} data-id="element-3293">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600" data-id="element-3294">
                                      {DATA_SOURCE_ICONS[source.type]}
                                    </div>
                                    <div className="flex-1 min-w-0" data-id="element-3295">
                                      <p className="text-sm font-medium text-gray-900 flex items-center gap-1" data-id="element-3296">
                                        {source.name}
                                        {source.type === 'weather' && source.link && <ExternalLink size={12} className="text-blue-500" data-id="element-3297" />}
                                        {source.type === 'incident' && source.nearMissReports && <span className="text-xs text-red-600 bg-red-100 px-1.5 py-0.5 rounded ml-1" data-id="element-3298">
                                              {source.nearMissReports.length}{' '}
                                              reports
                                            </span>}
                                        {source.type === 'ptp' && source.ptpDocument && <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded ml-1" data-id="element-3299">
                                              View Document
                                            </span>}
                                        {source.type === 'worker' && source.workers && <ChevronDown size={14} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-3300" />}
                                      </p>
                                      <p className="text-xs text-gray-500" data-id="element-3301">
                                        {source.description}
                                      </p>
                                    </div>
                                  </button>
                                  <AnimatePresence data-id="element-3302">
                                    {isExpanded && source.workers && <motion.div initial={{
                              opacity: 0,
                              height: 0
                            }} animate={{
                              opacity: 1,
                              height: 'auto'
                            }} exit={{
                              opacity: 0,
                              height: 0
                            }} className="mt-2 ml-11 space-y-2" data-id="element-3303">
                                        {source.workers.map(worker => <div key={worker.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-3304">
                                            <div className="flex items-start justify-between" data-id="element-3305">
                                              <div data-id="element-3306">
                                                <p className="font-semibold text-gray-900" data-id="element-3307">
                                                  {worker.name}
                                                </p>
                                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1" data-id="element-3308">
                                                  <Building2 size={12} data-id="element-3309" />
                                                  <span data-id="element-3310">
                                                    {worker.contractor}
                                                  </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1" data-id="element-3311">
                                                  <Calendar size={12} data-id="element-3312" />
                                                  <span data-id="element-3313">
                                                    First day:{' '}
                                                    {worker.firstDayOnSite.toLocaleDateString()}{' '}
                                                    ({worker.daysOnSite} days on
                                                    site)
                                                  </span>
                                                </div>
                                              </div>
                                              <a href={`tel:${worker.phone}`} className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors" onClick={e => e.stopPropagation()} data-id="element-3314">
                                                <Phone size={14} data-id="element-3315" />
                                                {worker.phone}
                                              </a>
                                            </div>
                                          </div>)}
                                      </motion.div>}
                                  </AnimatePresence>
                                </div>;
                      })}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 3: Corrective Actions */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3316">
                    <button onClick={() => toggleSection('actions')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3317">
                      <div className="flex items-center gap-3" data-id="element-3318">
                        <Lightbulb size={18} className="text-gray-500" data-id="element-3319" />
                        <h3 className="font-bold text-gray-900" data-id="element-3320">
                          Suggested Corrective Actions
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3321">
                        {reviewedSections.has('actions') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3322" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3323" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('actions') ? 'rotate-180' : ''}`} data-id="element-3324" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3325">
                      {openSections.has('actions') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3326">
                          <div className="p-5 border-t border-gray-200 space-y-4" data-id="element-3327">
                            <div className="space-y-2" data-id="element-3328">
                              {riskArea.correctiveActions.map((action, index) => {
                          const priorityConfig = PRIORITY_CONFIG[action.priority];
                          return <div key={action.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg" data-id="element-3329">
                                      <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-id="element-3330" />
                                      <div className="flex-1" data-id="element-3331">
                                        <p className="text-sm text-gray-900" data-id="element-3332">
                                          {action.action}
                                        </p>
                                        {action.assignedTo && <p className="text-xs text-gray-500 mt-0.5" data-id="element-3333">
                                            Assigned to: {action.assignedTo}
                                          </p>}
                                      </div>
                                      <span className={`text-xs font-semibold px-2 py-1 rounded ${priorityConfig.bgColor} ${priorityConfig.color}`} data-id="element-3334">
                                        {priorityConfig.label}
                                      </span>
                                    </div>;
                        })}
                            </div>

                            <div className="pt-2 border-t border-gray-100" data-id="element-3335">
                              {!creatingAction ? <button onClick={() => setCreatingAction(true)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5" data-id="element-3336">
                                  <Plus size={16} data-id="element-3337" /> Create Custom Action Item
                                </button> : <form onSubmit={handleCreateAction} className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3" data-id="element-3338">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-3339">
                                    New Action Item
                                  </h4>
                                  <div data-id="element-3340">
                                    <input type="text" required value={actionTitle} onChange={e => setActionTitle(e.target.value)} placeholder="Action description..." className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3341" />
                                  </div>
                                  <div className="grid grid-cols-3 gap-3" data-id="element-3342">
                                    <div data-id="element-3343">
                                      <input type="text" required value={actionOwner} onChange={e => setActionOwner(e.target.value)} placeholder="Assignee" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3344" />
                                    </div>
                                    <div data-id="element-3345">
                                      <input type="date" required value={actionDate} onChange={e => setActionDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3346" />
                                    </div>
                                    <div data-id="element-3347">
                                      <select value={actionPriority} onChange={e => setActionPriority(e.target.value as any)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3348">
                                        <option value="high" data-id="element-3349">
                                          High Priority
                                        </option>
                                        <option value="medium" data-id="element-3350">
                                          Medium Priority
                                        </option>
                                        <option value="low" data-id="element-3351">
                                          Low Priority
                                        </option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="flex justify-end gap-2 pt-2" data-id="element-3352">
                                    <button type="button" onClick={() => setCreatingAction(false)} className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded-md transition-colors" data-id="element-3353">
                                      Cancel
                                    </button>
                                    <button type="submit" className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors" data-id="element-3354">
                                      Save Action
                                    </button>
                                  </div>
                                </form>}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 4: Contractor Contacts */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3355">
                    <button onClick={() => toggleSection('contacts')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3356">
                      <div className="flex items-center gap-3" data-id="element-3357">
                        <User size={18} className="text-gray-500" data-id="element-3358" />
                        <h3 className="font-bold text-gray-900" data-id="element-3359">
                          Contractor Contacts
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3360">
                        {reviewedSections.has('contacts') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3361" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3362" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('contacts') ? 'rotate-180' : ''}`} data-id="element-3363" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3364">
                      {openSections.has('contacts') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3365">
                          <div className="p-5 border-t border-gray-200 grid grid-cols-2 gap-3" data-id="element-3366">
                            {riskArea.contacts.map((contact, index) => <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-3367">
                                <p className="font-semibold text-gray-900" data-id="element-3368">
                                  {contact.name}
                                </p>
                                <p className="text-xs text-gray-500 mb-2" data-id="element-3369">
                                  {contact.role} · {contact.company}
                                </p>
                                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-3370">
                                  <Phone size={14} data-id="element-3371" />
                                  {contact.phone}
                                </a>
                              </div>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 5: AI Chat */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3372">
                    <button onClick={() => toggleSection('aiChat')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3373">
                      <div className="flex items-center gap-3" data-id="element-3374">
                        <Sparkles size={18} className="text-indigo-500" data-id="element-3375" />
                        <h3 className="font-bold text-gray-900" data-id="element-3376">
                          Ask AI About This Risk
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3377">
                        {reviewedSections.has('aiChat') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3378" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3379" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('aiChat') ? 'rotate-180' : ''}`} data-id="element-3380" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3381">
                      {openSections.has('aiChat') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3382">
                          <div className="p-5 border-t border-gray-200" data-id="element-3383">
                            <RiskAIChat context="specific" riskArea={riskArea} data-id="element-3384" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div className="border-t border-gray-200 pt-6" data-id="element-3385">
                {!activeForm ? <div className="flex flex-col gap-3" data-id="element-3386">
                    {riskArea.status === 'open' && <>
                        <button onClick={() => setActiveForm('measures')} className="w-full py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2" data-id="element-3387">
                          <Clock size={18} data-id="element-3388" />
                          Mark Measures Taken
                        </button>
                        <button onClick={() => setActiveForm('close')} disabled={!allReviewed} className={`w-full py-3 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${allReviewed ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-3389">
                          <CheckCircle2 size={18} data-id="element-3390" />
                          {allReviewed ? 'Close Risk' : `Review ${remainingToReview} more section${remainingToReview === 1 ? '' : 's'} to close`}
                        </button>
                      </>}

                    {riskArea.status === 'measures_taken' && <>
                        <button onClick={() => setActiveForm('close')} disabled={!allReviewed} className={`w-full py-3 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${allReviewed ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-3391">
                          <CheckCircle2 size={18} data-id="element-3392" />
                          {allReviewed ? 'Close Risk' : `Review ${remainingToReview} more section${remainingToReview === 1 ? '' : 's'} to close`}
                        </button>
                        <button onClick={() => setActiveForm('reopen')} className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2" data-id="element-3393">
                          <RotateCcw size={18} data-id="element-3394" />
                          Reopen Risk
                        </button>
                      </>}

                    {riskArea.status === 'closed' && <button onClick={() => setActiveForm('reopen')} className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2" data-id="element-3395">
                        <RotateCcw size={18} data-id="element-3396" />
                        Reopen Risk
                      </button>}
                  </div> : <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="space-y-4" data-id="element-3397">
                    <div className="flex items-center gap-2 mb-4" data-id="element-3398">
                      {activeForm === 'measures' && <Clock className="text-amber-500" size={20} data-id="element-3399" />}
                      {activeForm === 'close' && <CheckCircle2 className="text-emerald-500" size={20} data-id="element-3400" />}
                      {activeForm === 'reopen' && <RotateCcw className="text-gray-500" size={20} data-id="element-3401" />}
                      <h4 className="font-bold text-gray-900" data-id="element-3402">
                        {activeForm === 'measures' && 'Record Measures Taken'}
                        {activeForm === 'close' && 'Close Risk Area'}
                        {activeForm === 'reopen' && 'Reopen Risk Area'}
                      </h4>
                    </div>

                    <div data-id="element-3403">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3404">
                        Your Name
                      </label>
                      <input type="text" value={userName} onChange={e => setUserName(e.target.value)} placeholder="Enter your name" className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent ${activeForm === 'measures' ? 'focus:ring-amber-500' : activeForm === 'close' ? 'focus:ring-emerald-500' : 'focus:ring-gray-500'}`} data-id="element-3405" />
                    </div>
                    <div data-id="element-3406">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3407">
                        {activeForm === 'measures' && 'What measures were taken?'}
                        {activeForm === 'close' && 'Resolution Notes'}
                        {activeForm === 'reopen' && 'Reason for reopening'}
                      </label>
                      <textarea value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Add details..." rows={3} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none ${activeForm === 'measures' ? 'focus:ring-amber-500' : activeForm === 'close' ? 'focus:ring-emerald-500' : 'focus:ring-gray-500'}`} data-id="element-3408" />
                    </div>
                    <div className="flex gap-3" data-id="element-3409">
                      <button onClick={resetForm} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-3410">
                        Cancel
                      </button>
                      <button onClick={() => {
                  if (activeForm === 'measures') handleSubmitStatusUpdate('measures_taken');else if (activeForm === 'close') handleSubmitStatusUpdate('closed');else if (activeForm === 'reopen') handleSubmitStatusUpdate('open');
                }} disabled={!formNotes.trim() || !userName.trim()} className={`flex-1 py-2.5 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${activeForm === 'measures' ? 'bg-amber-500 hover:bg-amber-600' : activeForm === 'close' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-800 hover:bg-gray-900'}`} data-id="element-3411">
                        Confirm
                      </button>
                    </div>
                  </motion.div>}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-500 relative" data-id="element-3412">
                <div className="flex items-center gap-4" data-id="element-3413">
                  <span data-id="element-3414">
                    Last updated: {riskArea.lastUpdated.toLocaleString()}
                  </span>
                  <span data-id="element-3415">Risk ID: {riskArea.id}</span>
                </div>

                <div data-id="element-3416">
                  <button onClick={() => setShowGeneralFeedback(!showGeneralFeedback)} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-3417">
                    <MessageCircle size={12} data-id="element-3418" />
                    Send Feedback
                  </button>

                  <AnimatePresence data-id="element-3419">
                    {showGeneralFeedback && <motion.div initial={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} className="absolute bottom-full right-4 mb-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50" data-id="element-3420">
                        <h4 className="font-semibold text-gray-900 mb-2 text-sm" data-id="element-3421">
                          Send Feedback
                        </h4>
                        <select value={feedbackType} onChange={e => setFeedbackType(e.target.value)} className="w-full text-xs p-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 mb-2" data-id="element-3422">
                          <option data-id="element-3423">Risk assessment feedback</option>
                          <option data-id="element-3424">AI response feedback</option>
                          <option data-id="element-3425">Feature suggestion</option>
                          <option data-id="element-3426">Other</option>
                        </select>
                        <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} placeholder="Tell us more..." className="w-full text-xs p-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none mb-2" rows={3} data-id="element-3427" />
                        <div className="flex justify-end gap-2" data-id="element-3428">
                          <button onClick={() => setShowGeneralFeedback(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1" data-id="element-3429">
                            Cancel
                          </button>
                          <button onClick={submitGeneralFeedback} disabled={!feedbackText.trim()} className="text-xs bg-gray-900 text-white px-3 py-1 rounded hover:bg-gray-800 disabled:opacity-50" data-id="element-3430">
                            Submit
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* PTP Document Viewer Modal */}
      <PTPDocumentViewer document={selectedPTPDocument} onClose={() => setSelectedPTPDocument(null)} data-id="element-3431" />

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-3432" />
    </>;
}